import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import MaterialService from "src/Services/API/material.service";
import AddMaterialTypeModal from "./AddMaterialTypeModal";
import EditDeleteDropdown from "./EditDeleteDropdown";
import ArchiveMaterialTypeModal from "./ArchiveMaterialTypeModal";

const MaterialCategoryComponent = ({ category, onDeleteCategory, onEditCategory }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchTypes = async () => {
    if (!category?.id) return;
    setIsLoading(true);
    try {
      const response = await MaterialService.getTypes(category.id);
      setTypes(response.data.filter((type) => type.isActive === true) || []);
    } catch (error) {
      toast.error("Failed to load material types");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddType = async () => {
    setShowAddModal(true);
  };

  const handleSaveType = async (name) => {
    if (!category?.id) return;
    try {
      await MaterialService.createType({ name, categoryId: category.id });
      toast.success("Material type added");
      setShowAddModal(false);
      fetchTypes();
    } catch (error) {
      toast.error("Failed to add material type");
    }
  };

  const handleEditType = (id) => {
    const target = types.find((t) => t.id === id || t?.id === Number(id));
    if (!target) return;
    setEditingType(target);
    setShowEditModal(true);
  };

  const handleSaveEditedType = async (name) => {
    if (!editingType?.id || !category?.id) return;
    try {
      await MaterialService.updateType(editingType.id, { name });
      toast.success("Material type updated");
      setShowEditModal(false);
      setEditingType(null);
      fetchTypes();
    } catch (error) {
      toast.error("Failed to update material type");
    }
  };

  const handleDeleteType = async (id) => {
    const target = types.find((t) => t.id === id || t?.id === Number(id));
    if (!target) return;
    setArchiveTarget(target);
    setShowArchiveModal(true);
  };

  return (
    <div className="rounded-[4px] border border-borderColor bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between px-4 py-3"
      >
        <span className="text-[12px] font-OpenSansRegular font-[600] uppercase text-titleColor">{category?.name}</span>
        <div className="flex items-center gap-2">
          <EditDeleteDropdown index={category.id} onDeleteClicked={onDeleteCategory} onEditClicked={onEditCategory} />
          <svg
            className={`h-4 w-4 text-titleColor transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-borderColor px-4 py-3">
          <div className="flex items-center justify-between text-[12px] font-OpenSansRegular text-subTitleColor">
            <div className="flex items-center gap-2">
              <span className="text-inputEmailTitle">Unit:</span>
              <span className="text-titleColor">{category?.unit || "N/A"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-inputEmailTitle">Mandatory?</span>
              <span className="text-titleColor">{category?.isMandatory ? "Yes" : "No"}</span>
            </div>
          </div>

          <div className="mt-3 flex flex-col divide-y divide-borderColor overflow-visible rounded-[4px] border border-borderColor">
            {isLoading && (
              <p className="px-3 py-2 text-[12px] font-OpenSansRegular text-subTitleColor">Loading types…</p>
            )}
            {!isLoading && types.length === 0 && (
              <p className="px-3 py-2 text-[12px] font-OpenSansRegular text-subTitleColor">No material types yet.</p>
            )}
            {!isLoading &&
              types.map((type) => (
                <div
                  key={type.id || type.name}
                  className="flex items-center justify-between px-3 py-2 text-[12px] font-OpenSansRegular text-titleColor"
                >
                  <span>{type.name}</span>
                  <EditDeleteDropdown
                    index={type.id}
                    onDeleteClicked={handleDeleteType}
                    onEditClicked={handleEditType}
                  />
                </div>
              ))}
          </div>

          <button
            type="button"
            onClick={handleAddType}
            className="mt-3 flex items-center gap-1 text-[12px] font-OpenSansRegular font-[600] text-subTitleColor"
          >
            <span className="text-[17px]">+ </span>
            <span>Add {category?.name}</span>
          </button>
        </div>
      )}
      {showAddModal && (
        <AddMaterialTypeModal
          categoryName={category?.name || "Material"}
          onCancel={() => setShowAddModal(false)}
          onSave={handleSaveType}
        />
      )}
      {showEditModal && (
        <AddMaterialTypeModal
          categoryName={category?.name || "Material"}
          title={`EDIT ${category?.name?.toUpperCase() || "MATERIAL"}`}
          initialName={editingType?.name || ""}
          onCancel={() => {
            setShowEditModal(false);
            setEditingType(null);
          }}
          onSave={handleSaveEditedType}
        />
      )}
      {showArchiveModal && (
        <ArchiveMaterialTypeModal
          name={archiveTarget?.name || ""}
          onCancel={() => {
            setShowArchiveModal(false);
            setArchiveTarget(null);
          }}
          onConfirm={async () => {
            if (!archiveTarget?.id) return;
            try {
              await MaterialService.updateType(archiveTarget.id, { isActive: false });
              toast.success("Material type archived");
              setShowArchiveModal(false);
              setArchiveTarget(null);
              fetchTypes();
            } catch (error) {
              toast.error("Failed to archive material type");
            }
          }}
        />
      )}
    </div>
  );
};

export default MaterialCategoryComponent;
